/**
 * "Upload recording" (F3-G, teacher/admin): reuses F3-F's resumable
 * video-upload component (`VideoUpload`) targeting the `class_session`; once
 * the video is ready, it links the recording to the session with
 * `POST /api/live/sessions/:id/recording`. From there on students see "View
 * recording".
 */
import { type Component, createSignal, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Upload } from "@fleet/icons";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { VideoUpload } from "@/features/media/components/video-upload";
import { liveApi, type LiveApi } from "../lib/live-api";

export interface RecordingAttachProps {
  sessionId: string;
  /** Accessible title (the session's topic). */
  title?: string;
  /** Inyectable en tests. */
  api?: LiveApi;
  /** Called after a successful link (to refresh the UI if needed). */
  onAttached?: (videoAssetId: string) => void;
}

export const RecordingAttach: Component<RecordingAttachProps> = (props) => {
  const { t } = useI18n();
  const api = () => props.api ?? liveApi;
  const [open, setOpen] = createSignal(false);

  return (
    <div data-testid="recording-attach">
      <Show
        when={open()}
        fallback={
          <Button
            variant="outline"
            size="sm"
            class="gap-1.5"
            data-testid="recording-attach-open"
            onClick={() => setOpen(true)}
          >
            <Upload class="h-4 w-4" /> {t(msg`Upload recording`)}
          </Button>
        }
      >
        <VideoUpload
          classSessionId={props.sessionId}
          onCancel={() => setOpen(false)}
          onUploaded={(asset) => {
            void (async () => {
              try {
                await api().attachRecording(props.sessionId, asset.id);
                showToast(t(msg`Recording linked to the class`), "success");
                props.onAttached?.(asset.id);
                setOpen(false);
              } catch {
                showToast(t(msg`Could not link the recording`), "error");
              }
            })();
          }}
        />
      </Show>
    </div>
  );
};
